import { Button, IconButton } from '@creditornot/cb-button';
import { ArrowLeft } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import { FORM_ERROR } from 'final-form';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import styled from 'styled-components';

import { getInboundMerchant, patchInboundMerchant } from 'apis/merchant-onboarding-service/api';
import { MOBILE_BREAKPOINT } from 'common/hooks/useIsMobile';
import { Typography as T } from 'common/typography';

import BusinessDetailsForm from '../forms/BusinessDetailsForm/BusinessDetailsForm';
import OpeningHoursForm from '../forms/OpeningHoursForm';
import { businessDetailsToApi, openingHoursToApi } from '../forms/transformer';
import {
  BusinessDetails,
  BusinessDetailsValidationSchema,
  OpeningHours,
  OpeningHoursValidationSchema,
} from '../forms/validation';

type BusinessInformationProps = {
  goToNextStep: () => void;
};

const Wrapper = styled.div``;

const ContentContainer = styled.div`
  margin-block-end: 32px;
`;

const Title = styled(T.Heading6)`
  padding-block-end: 16px;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    ${typographyCss.Title1}
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
`;

const StyledButton = styled(Button).attrs({
  size: 'large',
})`
  flex-grow: 1;
`;

const StyledArrowLeft = styled(ArrowLeft)`
  width: 20px;
`;

const BackButton = styled(IconButton).attrs({ variant: 'blue', size: 'small' })`
  border-radius: 12px;
  margin-inline-end: 12px;
  height: 56px;
  width: 56px;

  div {
    border-radius: 8px;
    height: 56px;
    width: 56px;
  }
`;

const BusinessInformation = ({ goToNextStep }: BusinessInformationProps) => {
  const { search } = useLocation();
  const history = useHistory();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  const searchParams = new URLSearchParams(search);
  const merchantId = searchParams.get('merchantId') || '';

  const { data: inboundMerchant } = useQuery(
    ['/merchant-admin/inbound-merchants', merchantId],
    () => getInboundMerchant(merchantId),
    {
      refetchOnWindowFocus: false,
    },
  );

  const BusinessDetailsFormId = 'onboarding-wizard-business-information-form';
  const OpeningHoursFormId = 'onboarding-wizard-opening-hours-form';

  const handleMovingOn = () => {
    const hasNextSection = currentSectionIndex < formData.length - 1;

    if (hasNextSection) {
      setCurrentSectionIndex(currentSectionIndex + 1);
    } else {
      goToNextStep();
    }
  };

  const handleMovingBack = () => {
    const isFirstSection = currentSectionIndex === 0;
    const hasPrevSection = currentSectionIndex > 0;

    if (hasPrevSection) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    } else if (isFirstSection) {
      history.push('/onboarding-welcome');
    }
  };

  const handleBusinessDetailsSubmit = async (
    values: BusinessDetails,
  ): Promise<{ [key: string]: string | Error } | void> => {
    try {
      const validFormValues = BusinessDetailsValidationSchema.validateSync(values);
      const transformedValues = businessDetailsToApi(validFormValues);

      await patchInboundMerchant(transformedValues, merchantId);

      handleMovingOn();
    } catch (submitError) {
      handleMovingOn(); // TODO: remove this line when the API is ready
      return { [FORM_ERROR]: submitError as Error };
    }
  };

  const handleOpeningHoursSubmit = async (values: OpeningHours): Promise<{ [key: string]: string | Error } | void> => {
    try {
      const validFormValues = OpeningHoursValidationSchema.validateSync(values);
      const transformedValues = openingHoursToApi(validFormValues);

      await patchInboundMerchant(transformedValues, merchantId);

      handleMovingOn();
    } catch (submitError) {
      handleMovingOn(); // TODO: remove this line when the API is ready
      return { [FORM_ERROR]: submitError as Error };
    }
  };

  const formData = [
    {
      title: 'Business details',
      formId: BusinessDetailsFormId,
      formComponent: (
        <BusinessDetailsForm
          country={inboundMerchant?.country || ''}
          handleSubmit={handleBusinessDetailsSubmit}
          formId={BusinessDetailsFormId}
        />
      ),
    },
    {
      title: 'Opening times',
      formId: OpeningHoursFormId,
      formComponent: <OpeningHoursForm handleSubmit={handleOpeningHoursSubmit} formId={OpeningHoursFormId} />,
    },
  ];

  const currentRenderContent = formData[currentSectionIndex];

  return (
    <Wrapper>
      {currentRenderContent && (
        <ContentContainer>
          <Title>{currentRenderContent.title}</Title>
          {currentRenderContent.formComponent}
        </ContentContainer>
      )}

      <ButtonsWrapper>
        <BackButton onClick={handleMovingBack}>
          <StyledArrowLeft />
        </BackButton>

        <StyledButton type="submit" form={currentRenderContent.formId} variant="blue">
          Continue
        </StyledButton>
      </ButtonsWrapper>
    </Wrapper>
  );
};

export default BusinessInformation;
